import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import {
  EntityStatus,
  PropertyDocumentStatus,
  PropertyDocumentType,
  PropertyDto,
  PropertyVerificationStatus,
} from '@workspace/data-types';

export interface PropertyStatusBadgeConfig {
  label: string;
  containerClass: string;
  dotClass: string;
}

@Component({
  selector: 'etxe-property-status-badge',
  templateUrl: './property-status-badge.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyStatusBadgeComponent {
  property = input.required<PropertyDto | null | undefined>();

  readonly config = computed<PropertyStatusBadgeConfig | null>(() => {
    const prop = this.property();
    if (!prop) return null;

    const verificationStatus = prop.verification?.owner ?? PropertyVerificationStatus.UNVERIFIED;
    const isVerified = verificationStatus === PropertyVerificationStatus.VERIFIED;
    const hasBeenPublished = !!prop.state?.firstActivatedAt;
    const isReadyToPublish =
      isVerified && prop.state?.status === EntityStatus.DEACTIVATED && !hasBeenPublished;

    if (isReadyToPublish) {
      return {
        label: 'Prête à publier',
        containerClass: 'bg-indigo-a3 text-indigo-11 border-indigo-a6',
        dotClass: 'bg-indigo-9 animate-pulse',
      };
    }

    switch (prop.state?.status) {
      case EntityStatus.ACTIVE:
        return {
          label: 'Publiée',
          containerClass: 'bg-jade-a3 text-jade-11 border-jade-a6',
          dotClass: 'bg-jade-9',
        };
      case EntityStatus.DEACTIVATED: {
        if (!isVerified) {
          const ownershipDocs =
            prop.documents?.filter((d) => d.type === PropertyDocumentType.OWNERSHIP) || [];
          const ownershipDoc = ownershipDocs.length > 0 ? ownershipDocs[ownershipDocs.length - 1] : undefined;
          const isPending = ownershipDoc?.status === PropertyDocumentStatus.PENDING_REVIEW;

          if (isPending) {
            return {
              label: 'En attente de validation',
              containerClass: 'bg-blue-a3 text-blue-11 border-blue-a6',
              dotClass: 'bg-blue-9',
            };
          }
          if (verificationStatus === PropertyVerificationStatus.REJECTED) {
            return {
              label: 'Justificatif rejeté',
              containerClass: 'bg-ruby-a3 text-ruby-11 border-ruby-a6',
              dotClass: 'bg-ruby-9',
            };
          }
          return {
            label: 'Non vérifié',
            containerClass: 'bg-amber-a3 text-amber-11 border-amber-a6',
            dotClass: 'bg-amber-9',
          };
        }
        return {
          label: 'Désactivé (masquée)',
          containerClass: 'bg-gray-a3 text-gray-11 border-gray-a6',
          dotClass: 'bg-ruby-9',
        };
      }
      case EntityStatus.DRAFT:
        return {
          label: 'Brouillon',
          containerClass: 'bg-orange-a3 text-orange-11 border-orange-a6',
          dotClass: 'bg-orange-9',
        };
      default:
        return null;
    }
  });
}
